import { useState, useEffect, useCallback, useRef } from 'react';
import { v2Api } from '@/lib/api';
import type { IDashboardData } from '@/types/dashboard';

export interface UseDashboardAnalyticsReturn {
  data: IDashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardAnalytics(): UseDashboardAnalyticsReturn {
  const [data, setData] = useState<IDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.dashboard.analytics.get();
      setData(data);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Dashboard data not found or failed to load.';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadDashboard();
    }
  }, [loadDashboard]);

  return { data, loading, error, refetch: loadDashboard };
}
